import { EnumRichiestaDTOStato } from '@framework/api-client/api/inquiries-compile/schemas/enumRichiestaDTOStato.ts';
import { GetFrontRichiesteStatoDelega } from '@framework/api-client/api/inquiries-compile/schemas/getFrontRichiesteStatoDelega.ts';
import { GetFrontRichiesteStatoTipoDomanda } from '@framework/api-client/api/inquiries-compile/schemas/getFrontRichiesteStatoTipoDomanda.ts';
import {
  InquiryTypeSelectInput,
  inquiryTypeChipValue,
} from '@framework/common-web/schema/inquiry-type/InquiryTypeSelectInput.tsx';
import { SelectInput } from '@framework/form/inputs/SelectInput.tsx';
import { createStaticChipValue } from '@framework/form/inputs/StaticChipValue.tsx';
import { t } from '@framework/i18n/intl/i18next.ts';
import {
  type SearchableField,
  SearchableFieldGroup,
  SearchableFieldType,
} from '@framework/ui/components/search/ISearchableField.tsx';
import { capitalize } from 'lodash';
import { useMemo } from 'react';
import {
  FrontCategoryAsyncSelectInput,
  frontCategoryChipValue,
} from './FrontCategoryAsyncSelectInput.tsx';

/** Stati ammissibili per il filtro stato tipo domadna (stati delle domande utilizzabili per la pubblicazione) */
const INQUIRY_STATUSES = [
  GetFrontRichiesteStatoTipoDomanda.PUBBLICATA,
  GetFrontRichiesteStatoTipoDomanda.APERTA,
  GetFrontRichiesteStatoTipoDomanda.IN_CORSO,
  GetFrontRichiesteStatoTipoDomanda.COLLAUDO,
  GetFrontRichiesteStatoTipoDomanda.CHIUSA,
];

/** Stati ammissibili per il filtro stato richiesta */
export const REQUEST_STATUSES = [
  // Lo stato "Creata" non deve essere visualizzabile
  // { label: 'Creata', value: EnumRichiestaDTOStato.CREATA },
  {
    label: t('submission-searchable-fields.bozza', 'Bozza'),
    value: EnumRichiestaDTOStato.BOZZA,
  },
  {
    label: t('submission-searchable-fields.integrata', 'Integrata'),
    value: EnumRichiestaDTOStato.INTEGRATA,
  },
  {
    label: t('submission-searchable-fields.inviata', 'Inviata'),
    value: EnumRichiestaDTOStato.INVIATA,
  },
  {
    label: t('submission-searchable-fields.ricevuta', 'Ricevuta'),
    value: EnumRichiestaDTOStato.RICEVUTA,
  },
  {
    label: t('submission-searchable-fields.conclusa', 'Conclusa'),
    value: EnumRichiestaDTOStato.CONCLUSA,
  },
  {
    label: t('submission-searchable-fields.annullata', 'Annullata'),
    value: EnumRichiestaDTOStato.ANNULLATA,
  },
  {
    label: t(
      'submission-searchable-fields.annullamento-in-corso',
      'Annullamento in corso',
    ),
    value: EnumRichiestaDTOStato.ANNULLAMENTO_IN_CORSO,
  },
];

/** Opzioni stato delega, condivise da select e chip della FilterBar. */
const STATO_DELEGA_OPTIONS = Object.values(GetFrontRichiesteStatoDelega).map(
  value => ({
    value,
    label: capitalize(value),
  }),
);

const staticSubmissionSearchableFields: SearchableField[] = [
  {
    type: SearchableFieldType.Text,
    label: t('submission-searchable-fields.oggetto', 'Oggetto'),
    name: 'oggetto',
    placeholder: t('submission-searchable-fields.oggetto', 'Oggetto'),
    group: SearchableFieldGroup.Filters,
  },
  {
    type: SearchableFieldType.Custom,
    label: t('submission-searchable-fields.stato-richiesta', 'Stato Richiesta'),
    name: 'statoRichiesta',
    group: SearchableFieldGroup.Filters,
    input: (
      <SelectInput
        name="statoRichiesta"
        placement="topLeft"
        size="middle"
        mode="multiple"
        allowClear
        placeholder={t(
          'submission-searchable-fields.stato-richiesta',
          'Stato Richiesta',
        )}
        options={REQUEST_STATUSES}
      />
    ),
    renderValue: createStaticChipValue(REQUEST_STATUSES),
  },
  {
    type: SearchableFieldType.Text,
    label: t('submission-searchable-fields.nome-titolare', 'Nome Titolare'),
    name: 'nomeTitolare',
    placeholder: t(
      'submission-searchable-fields.nome-titolare',
      'Nome Titolare',
    ),
    group: SearchableFieldGroup.Filters,
  },
  {
    type: SearchableFieldType.Text,
    label: t(
      'submission-searchable-fields.cognome-titolare',
      'Cognome Titolare',
    ),
    name: 'cognomeTitolare',
    placeholder: t(
      'submission-searchable-fields.cognome-titolare',
      'Cognome Titolare',
    ),
    group: SearchableFieldGroup.Filters,
  },
  {
    type: SearchableFieldType.Text,
    label: t(
      'submission-searchable-fields.codice-fiscale-titolare',
      'Codice Fiscale Titolare',
    ),
    name: 'codiceFiscaleTitolare',
    placeholder: t(
      'submission-searchable-fields.codice-fiscale-titolare',
      'Codice Fiscale Titolare',
    ),
    group: SearchableFieldGroup.Filters,
  },
  {
    type: SearchableFieldType.Custom,
    label: t('submission-searchable-fields.stato-delega', 'Stato Delega'),
    name: 'statoDelega',
    group: SearchableFieldGroup.Filters,
    input: (
      <SelectInput
        name="statoDelega"
        placement="topLeft"
        size="middle"
        allowClear
        placeholder={t(
          'submission-searchable-fields.stato-delega',
          'Stato Delega',
        )}
        options={STATO_DELEGA_OPTIONS}
      />
    ),
    renderValue: createStaticChipValue(STATO_DELEGA_OPTIONS),
  },
  {
    type: SearchableFieldType.Custom,
    label: t('submission-searchable-fields.categoria', 'Categoria'),
    name: 'categorie',
    group: SearchableFieldGroup.Filters,
    input: (
      <FrontCategoryAsyncSelectInput
        size="middle"
        name="categorie"
        placement="topLeft"
        mode="multiple"
        placeholder={t(
          'submission-searchable-fields.seleziona-le-categorie',
          'Seleziona le categorie',
        )}
      />
    ),
    renderValue: frontCategoryChipValue,
  },
  {
    type: SearchableFieldType.Text,
    label: t('submission-searchable-fields.ricerca-estesa', 'Ricerca Estesa'),
    name: 'valoriRicercaEstesa',
    placeholder: t(
      'submission-searchable-fields.ricerca-estesa',
      'Ricerca Estesa',
    ),
    group: SearchableFieldGroup.Filters,
  },
  {
    type: SearchableFieldType.Text,
    label: t('submission-searchable-fields.numero-istanza', 'Numero Istanza'),
    name: 'progressivo',
    placeholder: t(
      'submission-searchable-fields.numero-istanza',
      'Numero Istanza',
    ),
    group: SearchableFieldGroup.Filters,
  },
  {
    type: SearchableFieldType.Number,
    label: t('submission-searchable-fields.anno-istanza', 'Anno Istanza'),
    name: 'progressivoAnno',
    placeholder: t('submission-searchable-fields.anno-istanza', 'Anno Istanza'),
    group: SearchableFieldGroup.Filters,
  },
  {
    type: SearchableFieldType.Text,
    label: t(
      'submission-searchable-fields.titolo-tipo-domanda',
      'Titolo Tipo Domanda',
    ),
    name: 'titoloTipoDomanda',
    placeholder: t(
      'submission-searchable-fields.titolo-tipo-domanda',
      'Titolo Tipo Domanda',
    ),
    group: SearchableFieldGroup.Filters,
  },
  {
    type: SearchableFieldType.Custom,
    label: t('submission-searchable-fields.tipo-domanda', 'Tipo Domanda'),
    name: 'idGruppoTipoDomanda',
    group: SearchableFieldGroup.Filters,
    input: (
      <InquiryTypeSelectInput
        name="idGruppoTipoDomanda"
        placement="topLeft"
        placeholder={t(
          'submission-searchable-fields.tipo-domanda',
          'Tipo Domanda',
        )}
        size="middle"
      />
    ),
    renderValue: inquiryTypeChipValue,
  },
  {
    type: SearchableFieldType.DateRange,
    label: t('submission-searchable-fields.data-creazione', 'Data creazione'),
    name: 'dataCreazione',
    group: SearchableFieldGroup.Filters,
    from: {
      name: 'dataAvvioCompilazioneDa',
      label: t(
        'submission-searchable-fields.data-avvio-compilazione-dal',
        'Data avvio compilazione dal',
      ),
    },
    to: {
      name: 'dataAvvioCompilazioneA',
      label: t(
        'submission-searchable-fields.data-avvio-compilazione-al',
        'Data avvio compilazione al',
      ),
    },
  },
  {
    type: SearchableFieldType.DateRange,
    label: t('submission-searchable-fields.data-invio', 'Data invio'),
    name: 'dataInvio',
    group: SearchableFieldGroup.Filters,
    from: {
      name: 'dataInvioCompilazioneDa',
      label: t('submission-searchable-fields.data-invio-dal', 'Data invio dal'),
    },
    to: {
      name: 'dataInvioCompilazioneA',
      label: t('submission-searchable-fields.data-invio-al', 'Data invio al'),
    },
  },
  {
    type: SearchableFieldType.Text,
    label: t(
      'submission-searchable-fields.titolare-denominazione-impresa',
      'Titolare Impresa: Denominazione',
    ),
    name: 'titolarePrincipaleDenominazioneImpresa',
    placeholder: t(
      'submission-searchable-fields.soggetto-richiesta-nome-placeholder',
      'Impresa...',
    ),
    group: SearchableFieldGroup.Filters,
  },
  {
    type: SearchableFieldType.Text,
    label: t(
      'submission-searchable-fields.titolare-partita-iva-impresa',
      'Titolare Impresa: Partita IVA',
    ),
    name: 'titolarePrincipalePartitaIva',
    placeholder: t(
      'submission-searchable-fields.titolare-partita-iva-impresa-placeholder',
      'Partita IVA...',
    ),
    group: SearchableFieldGroup.Filters,
  },
  {
    type: SearchableFieldType.Text,
    label: t(
      'submission-searchable-fields.titolare-codice-fiscale-impresa',
      'Titolare Impresa: Codice Fiscale',
    ),
    name: 'titolarePrincipaleCodiceFiscaleImpresa',
    placeholder: t(
      'submission-searchable-fields.titolare-codice-fiscale-impresa-placeholder',
      'Codice fiscale...',
    ),
    group: SearchableFieldGroup.Filters,
  },
  {
    type: SearchableFieldType.Text,
    label: t(
      'submission-searchable-fields.titolare-codice-rea-impresa',
      'Titolare Impresa: Codice REA',
    ),
    name: 'titolarePrincipaleCodiceReaImpresa',
    placeholder: t(
      'submission-searchable-fields.titolare-codice-rea-impresa-placeholder',
      'Codice REA...',
    ),
    group: SearchableFieldGroup.Filters,
  },
];

export function useSubmissionSearchableFields(options: {
  extraFields?: SearchableField[];
}) {
  const searchableFields: SearchableField[] = useMemo(
    () =>
      [
        ...staticSubmissionSearchableFields,
        ...(options.extraFields ?? []),
      ].sort((a, b) => (a.label > b.label ? 1 : -1)),
    [options.extraFields],
  );

  return searchableFields;
}
